import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormControl, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { StateService } from '../../services/state.service';
import { FreemiumService } from '../../services/freemium.service';
import { RegisterTrialEmailComponent } from './register-trial-email/register-trial-email.component';
import { TrialStepTwoComponent } from './trial-step-two/trial-step-two.component';
import { TrialStepThreeComponent } from './trial-step-three/trial-step-three.component';
import { TrialStepFourComponent } from './trial-step-four/trial-step-four.component';
import { TrialStepIndicatorComponent } from './trial-step-indicator/trial-step-indicator.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';
import { SelectItem } from '../../shared/components/select-menu/select-menu.component';
import { TokenService } from '../../services/token.service';
import { AssetVersionPipe } from '../../pipes/asset-version.pipe';
import { TrialStep } from '../../shared/types/common';
import { HubspotUtm } from '../../services/hubspot/hubspot.types';

@Component({
  selector: 'app-trial-screen',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RegisterTrialEmailComponent,
    TrialStepTwoComponent,
    TrialStepThreeComponent,
    TrialStepFourComponent,
    TrialStepIndicatorComponent,
    AssetVersionPipe
  ],
  templateUrl: './trial-screen.component.html',
  styleUrl: './trial-screen.component.scss',
})
export class TrialScreenComponent implements OnInit {
  private stateService = inject(StateService);
  private freemiumService = inject(FreemiumService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private tokenService = inject(TokenService);

  readonly TrialStep = TrialStep;
  currentStep = TrialStep.REGISTER_EMAIL;

  private forbiddenDomains = [
    'gmail.com',
    'yahoo.com',
    'outlook.com',
    'hotmail.com',
    'aol.com',
    'icloud.com',
    'mail.com',
    'protonmail.com',
    'zoho.com',
    'gmx.com'
  ];

  firstNameControl = new FormControl('', [Validators.required]);
  lastNameControl = new FormControl('', [Validators.required]);
  emailControl = new FormControl('', [
    Validators.required,
    this.emailFormatValidator.bind(this),
    this.workEmailValidator.bind(this)
  ]);

  companyNameControl = new FormControl<string | null>(null, [Validators.required]);
  jobTitleControl = new FormControl<string | null>(null, [Validators.required]);

  jobTitleItems: SelectItem<string>[] = [];

  ngOnInit(): void {
    this.stateService.loading = true;
    this.checkQueryParams();
  }

  private loadSignupOptions(): void {
    this.freemiumService.getSignupOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.jobTitleItems = response.jobTitles.map(title => ({
            label: title,
            key: title
          }));
        },
        error: (error) => {
          console.error('Error loading signup options:', error);
          this.jobTitleItems = [];
        }
      });
  }

  private checkQueryParams(): void {
    const queryParams = this.route.snapshot.queryParams;
    const email = queryParams['email'];
    const signupToken = queryParams['signupToken'];
    const id = queryParams['id'];
    const utms: Partial<HubspotUtm> = {
      utm_source: queryParams['utm_source'],
      utm_medium: queryParams['utm_medium'],
      utm_campaign: queryParams['utm_campaign'],
      utm_content: queryParams['utm_content']
    }

    if (email && signupToken && id) {
      this.handleExistingUser(email, signupToken, id, utms);
    } else {
      this.stateService.loading = false;
    }
  }

  private handleExistingUser(email: string, signupToken: string, id: string, utms?: Partial<HubspotUtm>): void {
    this.freemiumService.getTrialUser(email, signupToken, id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const hubspotFields = {};
          if (response.token) {
            this.tokenService.setToken(response.token.access_token);
          }
          this.firstNameControl.setValue(response.firstName);
          this.lastNameControl.setValue(response.lastName);
          this.emailControl.setValue(response.email);

          Object.assign(hubspotFields, {
            email: response.email,
            firstname: response.firstName,
            lastname: response.lastName,
          })

          if (response.companyName && response.jobTitle) {
            this.companyNameControl.setValue(response.companyName);
            this.jobTitleControl.setValue(response.jobTitle);
            this.currentStep = TrialStep.COMPLETE;

            Object.assign(hubspotFields, {
              company: response.companyName,
              jobtitle: response.jobTitle
            })
          }
          else if (response.accountKey) {
            this.currentStep = TrialStep.PROFILE;
          }
          else if (response.email) {
            this.currentStep = TrialStep.SELECT_BRAND;
          }

          this.freemiumService.updateHubspotFields(hubspotFields);
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {...utms},
            replaceUrl: true
          });
          this.stateService.loading = false;
        },
        error: () => {
          this.emailControl.setValue(email);
          this.currentStep = TrialStep.SELECT_BRAND;
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {...utms},
            replaceUrl: true
          });
        }
      });
    this.loadSignupOptions();
  }

  private emailFormatValidator(control: AbstractControl): ValidationErrors | null {
    const email = control.value;
    if (!email) return null;

    // Pattern: string@string.string (requires domain with TLD)
    const emailPattern = /^[^@]+@[^@]+\.[^@]+$/;
    if (!emailPattern.test(email)) {
      return { email: true };
    }
    return null;
  }

  private workEmailValidator(control: AbstractControl): ValidationErrors | null {
    const email = control.value;
    if (!email) return null;

    const domain = email.split('@')[1]?.toLowerCase();
    if (domain && this.forbiddenDomains.includes(domain)) {
      return { forbiddenDomain: true };
    }
    return null;
  }

  onNextStep(): void {
    if (this.currentStep === TrialStep.REGISTER_EMAIL) {
      this.loadSignupOptions();
    }
    if (this.currentStep < TrialStep.COMPLETE) {
      this.currentStep++;
    }
  }
}
